import { useEffect, useRef, useState } from 'react';
import ShelfQrScanner from './ShelfQrScanner';
import { dhlIntake, dhlLookup, dhlNotiz, dhlRein, istDhlCode, istDhlScanCode, dhlKurz, dhlPlatzhalterName, isDhlLabelMitNameOn, DHL_NACHLADEN_MS, DHL_LABEL_WARTE_MS, dt } from '../services/dhlBridge';
import { isDhlStickEnabled, enqueueDhlStickSchnelleinlagerung } from '../services/dhlUsbStick';
import { isAutoRegalLetter, getCarrierSortMode } from '../services/printerService';
import { autoRegalFach } from '../services/regalUnterfach';
import { uploadFoto } from '../services/mlService';
import { isTurboOn } from '../services/turbo';
import TurboAnzeige from './TurboAnzeige';
import FotoStand, { useFotoZaehler, fotosOffen } from './FotoStand';
import { mitWiederholung } from '../services/serverWarte';

const autoNachnameDhl = () => {
  const m = getCarrierSortMode('DHL');
  return m ? m === 'nachname' : isAutoRegalLetter();
};

// Foto vom Erkennen (Daten-URL) in der Bild-Ablage sichern, Ergebnis ist die Bild-ID fuers Paket; scheitert es, bleibt das Paket ohne Foto
async function fotoSichern(dataUrl) {
  try { return await uploadFoto(await (await fetch(dataUrl)).blob()); } catch { return ''; }
}

/**
 * 🧪 „DHL erfassen" (Experiment): Barcodes der Pakete vom Fahrer nacheinander scannen. Jeder Code geht im
 * Hintergrund an das DHL-ServicePoint-Portal („Bezorger komt leveren → Alleen inscannen"); kommt der Name zurück,
 * wird das Paket mit Namen angelegt. Rückfall: antwortet DHL nicht, wird es trotzdem angelegt (Platzhalter
 * „DHL 123-456") und als „bitte im DHL-Portal scannen" markiert. Töne: hoch = ok, tief = Problem.
 */
let audioCtx = null;
function ton(freq, ms, type = 'sine') {
  try {
    audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const osc = audioCtx.createOscillator(); const gain = audioCtx.createGain();
    osc.type = type; osc.frequency.value = freq; gain.gain.value = 0.2;
    osc.connect(gain); gain.connect(audioCtx.destination); osc.start(); osc.stop(audioCtx.currentTime + ms / 1000);
  } catch { /* kein Ton möglich */ }
}
const tonOk = () => ton(880, 120);
const tonFehler = () => ton(220, 450, 'square');

export default function DhlErfassen({ pakete, colors, language, onAnlegen, onNamen, onDrucken, onClose }) {
  const T = dt(language);
  const dark = colors?.darkMode;
  const f = { text: dark ? '#fff' : '#23272f', sub: dark ? '#aaa' : '#666', rand: dark ? '#444' : '#ddd', feld: dark ? '#1b1f27' : '#fff', zeile: dark ? '#2d333b' : '#f5f6f8' };

  const [fach, setFach] = useState('');
  const [hand, setHand] = useState('');
  const [eintraege, setEintraege] = useState([]); // {code, stand, name, fach, grund}
  const fachRef = useRef(''); fachRef.current = fach;
  const paketeRef = useRef(pakete); paketeRef.current = pakete;
  const anlegenRef = useRef(onAnlegen); anlegenRef.current = onAnlegen;
  const namenRef = useRef(onNamen); namenRef.current = onNamen;
  const gesehen = useRef(new Set());
  const fotos = useRef(new Map()); // Code -> Foto vom Erkennen (wird am Paket gesichert, wie bei Vinted)
  const kette = useRef(Promise.resolve());
  const [fotoStand, verfolgeFoto] = useFotoZaehler(); // 📷 PaketPilot-Foto lädt im Hintergrund
  const lebt = useRef(true);
  useEffect(() => () => { lebt.current = false; }, []);

  // Offene „DHL 123-456"-Platzhalter aus früheren Scans beim Öffnen im Hintergrund auflösen (~150 ms per JSON-API)
  useEffect(() => {
    const offene = (paketeRef.current || []).filter((p) => p && !p.deleted && !p.ausgegebenAm && !p.zurueckgeschickt && /^DHL [A-Z0-9]{3}-[A-Z0-9]{3}$/i.test(String(p.name || '').trim()) && p.trackingNumber).slice(0, 15);
    if (!offene.length || !namenRef.current) return;
    (async () => {
      const updates = [];
      for (const p of offene) {
        try {
          const l = await dhlLookup(p.trackingNumber);
          if (l && l.found && l.name) {
            const rFach = p.regalplatz || (autoNachnameDhl() ? autoRegalFach(l.name) || '' : '');
            updates.push({ paket: p, code: dhlKurz(p.trackingNumber), name: l.name, regalplatz: rFach || undefined });
          }
        } catch { /* */ }
      }
      if (updates.length && namenRef.current) { try { await namenRef.current(updates); } catch { /* */ } }
    })();
  }, []);

  const setze = (code, patch) => { if (lebt.current) setEintraege((alt) => alt.map((e) => (e.code === code ? { ...e, ...patch } : e))); };
  const norm = (s) => dhlRein(s);
  const meins = (code) => (paketeRef.current || []).find((p) => p && !p.deleted && !p.ausgegebenAm && !p.zurueckgeschickt && norm(p.trackingNumber) === norm(code));

  // ⚡ Turbo (Experiment): Foto im Hintergrund, großes Fach, Zähler, Zeitmessung je Paket
  const [turbo] = useState(() => isTurboOn());
  const [turboStand, setTurboStand] = useState({ letzter: null, gebucht: 0, ausnahmen: 0, summeMs: 0, n: 0 });
  const scanZeit = useRef(new Map());
  const turboMelden = (letzter, dauerMs) => {
    if (!turbo || !lebt.current) return;
    setTurboStand((s) => ({
      letzter,
      gebucht: s.gebucht + (letzter.ausnahme === 'fremd' || letzter.ausnahme === 'doppelt' ? 0 : 1),
      ausnahmen: s.ausnahmen + (letzter.ausnahme ? 1 : 0),
      summeMs: s.summeMs + (dauerMs || 0), n: s.n + (dauerMs ? 1 : 0),
    }));
  };

  const verarbeite = async (code) => {
    const foto = fotos.current.get(code) || '';
    fotos.current.delete(code);
    const t0 = scanZeit.current.get(code) || Date.now();
    scanZeit.current.delete(code);
    const bildId = foto ? fotoSichern(foto) : Promise.resolve('');
    if (foto && !turbo) verfolgeFoto(bildId.then((id) => !!id, () => false));
    // 🔌 Parallel an den physischen DHL USB-Stick (Schnelleinlagerung Queue + 45s Fast-Path) senden
    if (isDhlStickEnabled()) {
      enqueueDhlStickSchnelleinlagerung({ barcodes: [code] }).catch(() => {});
    }
    // ⏳ Server kurz weg (Deploy/WLAN)? Scan bleibt in der Schlange und wird nachgeholt
    const r = await mitWiederholung(() => dhlIntake(code), { onWarte: ({ versuch }) => setze(code, { stand: 'wartet', grund: T('serverWarte', { n: versuch }) }) });
    setze(code, { grund: '' });
    const tGebucht = Date.now();
    if (!lebt.current) return;
    const name = (r.ok && r.name) || dhlPlatzhalterName(code);
    const fachJetzt = fachRef.current.trim() || (autoNachnameDhl() && r.ok && r.name ? autoRegalFach(r.name) || '' : '');
    if (r.reason === 'abgelehnt') { tonFehler(); setze(code, { stand: 'abgelehnt', grund: r.message || '' }); turboMelden({ fach: '✗', name: '', kurz: dhlKurz(code), ausnahme: 'fremd' }); return; }
    // Angenommen, aber noch ohne Namen: Das Portal zeigt ein frisch gescanntes Paket erst später in „Voorraad → Klant"
    // (13.10.2026: beim Scan nie, Minuten später ja). Mit „Etikett erst mit Name" wird das Paket ohne Druck angelegt,
    // die Zeile zeigt einen Ladebalken, der Name wird nachgeschlagen und dann gedruckt (sonst nach der Wartezeit mit Nummer).
    const ohneName = r.ok && !r.name;
    const wartenAufName = ohneName && isDhlLabelMitNameOn() && !!onDrucken;
    try {
      // ⚡ Turbo: nicht auf den Foto-Upload warten — das Foto kommt danach ans Paket
      const neu = await anlegenRef.current({ tracking: code, name, regalplatz: fachJetzt, dienst: 'DHL', imageUrl: turbo ? '' : await bildId, drucken: !wartenAufName });
      const tAngelegt = Date.now();
      if (turbo && neu && neu.id) {
        verfolgeFoto(bildId.then(async (id) => {
          if (!id || !namenRef.current) return !!id;
          const p = (paketeRef.current || []).find((x) => x && x.id === neu.id) || neu;
          await namenRef.current([{ paket: p, code: dhlKurz(code), name: p.name || name, imageUrl: id, nurFoto: true }]);
          return true;
        }).catch(() => false));
      }
      const zeiten = turbo ? `Turbo: gebucht ${tGebucht - t0} ms, angelegt ${tAngelegt - t0} ms` : '';
      const msg = [wartenAufName ? 'Etikett wartet auf den Namen' : '', zeiten].filter(Boolean).join(' · ');
      dhlNotiz('annahme_paketpilot', code, { ok: true, ...(msg ? { message: msg } : {}) });
      if (!r.ok) dhlNotiz('rueckfall', code, { reason: r.reason || 'unbekannt', message: 'Annahme: in PaketPilot angelegt, bei DHL nicht bestätigt' });
      (r.ok ? tonOk : tonFehler)();
      setze(code, { stand: r.ok ? (r.schonImBestand ? 'schon' : r.name ? 'ok' : wartenAufName ? 'wartetName' : 'okOhneName') : 'ohne', name, fach: fachJetzt, grund: r.reason || '', seit: Date.now() });
      if (ohneName) namenNachladen(code, 0, wartenAufName);
      const ausnahme = !r.ok ? 'rueckfall' : !r.name ? 'keinName' : !fachJetzt && autoNachnameDhl() ? 'keinFach' : '';
      turboMelden({ fach: fachJetzt, name, kurz: dhlKurz(code), ausnahme }, tAngelegt - t0);
    } catch (e) {
      tonFehler();
      dhlNotiz('annahme_paketpilot', code, { ok: false, message: String((e && e.message) || e).slice(0, 200) });
      setze(code, { stand: 'ohne', name, grund: 'paketpilot' });
      turboMelden({ fach: fachJetzt, name, kurz: dhlKurz(code), ausnahme: 'rueckfall' });
    }
  };

  // Namen für ein Paket ohne Namen später nachschlagen (20, 45, 80, 125, 180 s nach dem Scan) und auf das Paket übertragen.
  // Läuft NEBEN der Scan-Kette: die Brücke beantwortet das Nachschlagen aus ihrem zweiten Tab, Scans warten nicht darauf.
  // drucken=true („Etikett erst mit Name"): sobald der Name da ist, Fach dazu und Etikett drucken; kommt keiner, nach dem
  // letzten Versuch mit der Nummer drucken. Das Nachschlagen selbst steht im Protokoll (Route /dhl/lookup).
  const namenNachladen = (code, versuch = 0, drucken = false) => {
    if (versuch >= DHL_NACHLADEN_MS.length) {
      if (drucken) { const p = meins(code); if (p && onDrucken) { try { onDrucken(p); } catch { /* */ } } setze(code, { stand: 'okOhneName', gedruckt: 'ohne' }); }
      return;
    }
    // Läuft auch weiter, wenn der Dialog schon geschlossen ist (Etikett muss trotzdem kommen); setze() ignoriert dann die Anzeige
    setTimeout(async () => {
      try {
        const l = await dhlLookup(code);
        const p = meins(code);
        if (l && l.found && l.name) {
          const fach = (p && p.regalplatz) || fachRef.current.trim() || (autoNachnameDhl() ? autoRegalFach(l.name) || '' : '');
          if (p && namenRef.current) { try { await namenRef.current([{ paket: p, code: dhlKurz(code), name: l.name, regalplatz: fach || undefined }]); } catch { /* */ } }
          if (drucken && p && onDrucken) { try { onDrucken({ ...p, name: l.name, regalplatz: fach || p.regalplatz || '' }); } catch { /* */ } }
          setze(code, { stand: 'ok', name: l.name, fach, grund: '', gedruckt: drucken ? 'mit' : '' });
          return;
        }
      } catch { /* nächster Versuch */ }
      namenNachladen(code, versuch + 1, drucken);
    }, DHL_NACHLADEN_MS[versuch]);
  };

  // Rückfall-Eintrag noch einmal an DHL geben; klappt es, bekommt der Platzhalter den Namen
  const nochmal = (code) => {
    setze(code, { stand: 'wartet', grund: '' });
    kette.current = kette.current.then(async () => {
      const r = await dhlIntake(code);
      if (!lebt.current) return;
      if (!r.ok) { tonFehler(); setze(code, { stand: r.reason === 'abgelehnt' ? 'abgelehnt' : 'ohne', grund: r.message || r.reason || '' }); return; }
      tonOk();
      const p = meins(code);
      if (p && r.name && namenRef.current) { try { await namenRef.current([{ paket: p, code: dhlKurz(code), name: r.name }]); } catch { /* */ } }
      dhlNotiz('annahme_paketpilot', code, { ok: true, message: 'nach Rückfall erneut an DHL gegeben' });
      setze(code, { stand: r.name ? 'ok' : 'okOhneName', name: r.name || dhlPlatzhalterName(code), grund: '' });
    }).catch(() => {});
  };

  const neuerCode = (roh, foto, vollFoto, alle, manuell = false) => {
    const pruefe = manuell ? istDhlCode : istDhlScanCode;
    const kandidaten = (Array.isArray(alle) && alle.length ? alle : [roh]).map(norm).filter(pruefe);
    const code = kandidaten.find(istDhlScanCode) || kandidaten[0] || '';
    if (!pruefe(code) || gesehen.current.has(code)) return;
    gesehen.current.add(code);
    scanZeit.current.set(code, Date.now());
    if (vollFoto || foto) fotos.current.set(code, vollFoto || foto);
    const schonDa = !!meins(code);
    if (schonDa && turbo) { const p = meins(code); turboMelden({ fach: (p && p.regalplatz) || '—', name: (p && p.name) || '', kurz: dhlKurz(code), ausnahme: 'doppelt' }); }
    setEintraege((alt) => [{ code, stand: schonDa ? 'doppelt' : 'wartet', name: '', grund: '' }, ...alt]);
    if (!schonDa) kette.current = kette.current.then(() => verarbeite(code)).catch(() => {});
  };

  const standText = (e) => ({ wartet: `⏳ ${T('wartet')}`, ok: `✅ ${T('okDhl')}${e.gedruckt === 'mit' ? ` · 🖨️ ${T('gedrucktMitName')}` : ''}`, okOhneName: `✅ ${e.gedruckt === 'ohne' ? T('gedrucktOhneName') : T('okOhneName')}`, wartetName: `⏳ ${T('wartetName')}`, schon: `ℹ️ ${T('schon')}`, ohne: `⚠️ ${T('ohneDhl')}`, abgelehnt: `✗ ${T('abgelehnt')}`, doppelt: `↩️ ${T('doppelt')}` }[e.stand]);
  // Ladebalken für „wartet auf den Namen": jede Sekunde neu zeichnen, solange eine Zeile wartet
  const [, setTick] = useState(0);
  const wartetJemand = eintraege.some((e) => e.stand === 'wartetName');
  useEffect(() => { if (!wartetJemand) return undefined; const t = setInterval(() => setTick((x) => x + 1), 1000); return () => clearInterval(t); }, [wartetJemand]);
  const fortschritt = (e) => Math.min(100, Math.round(((Date.now() - (e.seit || Date.now())) / DHL_LABEL_WARTE_MS) * 100));
  const feld = { width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${f.rand}`, fontSize: 15, background: f.feld, color: f.text, boxSizing: 'border-box' };
  const laeuft = eintraege.some((e) => e.stand === 'wartet');

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 7600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div data-testid="dhl-erfassen" style={{ background: dark ? '#23272f' : '#fff', color: f.text, borderRadius: 14, width: '100%', maxWidth: 560, maxHeight: '92vh', overflowY: 'auto', padding: 18, boxShadow: '0 10px 40px rgba(0,0,0,0.4)' }}>
        <b style={{ fontSize: 18 }}>🧪 {T('erfassenTitel')}</b>
        <div data-testid="de-live" style={{ marginTop: 8, padding: '6px 10px', borderRadius: 8, background: '#c62828', color: '#fff', fontSize: 13, fontWeight: 700 }}>🔴 {T('liveBadge')}</div>
        <div style={{ fontSize: 13, color: f.sub, marginTop: 6, lineHeight: 1.5 }}>{T('erfassenHinweis')}</div>
        {turbo && <TurboAnzeige language={language} dark={dark} letzter={turboStand.letzter} gebucht={turboStand.gebucht} ausnahmen={turboStand.ausnahmen} schnittMs={turboStand.n ? turboStand.summeMs / turboStand.n : 0} wartend={eintraege.filter((e) => e.stand === 'wartet').length} />}
        <FotoStand stand={fotoStand} dark={dark} text={(a, b) => T('fotosLaden', { fertig: a, gesamt: b })} fehlerText={(n) => T('fotosFehler', { n })} />
        <input data-testid="de-fach" placeholder={isAutoRegalLetter() ? T('fachAuto') : T('fach')} value={fach} onChange={(e) => setFach(e.target.value)} style={{ ...feld, marginTop: 12 }} />
        <div style={{ marginTop: 12 }}>
          <ShelfQrScanner active eingebettet mitFoto vollbildMoeglich filter={istDhlScanCode} formats={['code_128', 'qr_code', 'itf', 'data_matrix', 'code_39', 'codabar']} onCode={(text, alle, foto, vollFoto) => neuerCode(text, foto, vollFoto, alle, false)}
            vollbildInfo={eintraege.length || fotoStand.gesamt ? [<FotoStand key="fotos" hell stand={fotoStand} text={(a, b) => T('fotosLaden', { fertig: a, gesamt: b })} fehlerText={(n) => T('fotosFehler', { n })} />, ...eintraege.slice(0, 3).map((e, i) => (
              <div key={e.code} style={{ padding: '5px 0', fontSize: i === 0 ? 17 : 14, opacity: i === 0 ? 1 : 0.75, borderTop: i ? '1px solid rgba(255,255,255,0.15)' : 'none' }}>
                <b>{dhlKurz(e.code)}</b>{e.name ? ` · ${e.name}` : ''}{e.fach ? ` · 📍 ${e.fach}` : ''}
                <div style={{ fontSize: 13, color: e.stand === 'ohne' || e.stand === 'abgelehnt' ? '#ff8a80' : '#cfd8dc' }}>{standText(e)}</div>
                {e.stand === 'wartetName' && (
                  <div style={{ marginTop: 4, height: 5, borderRadius: 3, background: 'rgba(255,255,255,0.2)', overflow: 'hidden' }}>
                    <div style={{ width: `${fortschritt(e)}%`, height: '100%', background: '#ffcc00', transition: 'width 1s linear' }} />
                  </div>
                )}
              </div>
            ))] : null} />
        </div>
        <form onSubmit={(e) => { e.preventDefault(); neuerCode(hand, '', '', [hand], true); setHand(''); }} style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <input data-testid="de-hand" placeholder={T('codeTippen')} value={hand} onChange={(e) => setHand(e.target.value)} style={feld} />
          <button type="submit" data-testid="de-hand-ok" style={{ padding: '0 14px', borderRadius: 8, border: 'none', background: '#d40511', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>{T('hinzufuegen')}</button>
        </form>
        <div data-testid="de-liste" style={{ marginTop: 12 }}>
          {eintraege.map((e) => (
            <div key={e.code} data-stand={e.stand} style={{ padding: '8px 10px', borderRadius: 8, background: f.zeile, marginTop: 6, fontSize: 14 }}>
              <b>{dhlKurz(e.code)}</b> <span style={{ color: f.sub, fontSize: 12 }}>{e.code}</span>{e.name ? ` · ${e.name}` : ''}{e.fach ? ` · 📍 ${e.fach}` : ''}
              <div style={{ fontSize: 13, color: e.stand === 'ohne' || e.stand === 'abgelehnt' ? '#c62828' : f.sub, marginTop: 2, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ flex: 1 }}>{standText(e)}{e.grund && e.stand !== 'ok' && e.stand !== 'okOhneName' && e.stand !== 'wartetName' ? ` (${e.grund})` : ''}</span>
                {e.stand === 'ohne' && <button type="button" data-testid={`de-nochmal-${e.code}`} onClick={() => nochmal(e.code)} style={{ padding: '4px 10px', borderRadius: 8, border: `1px solid ${f.rand}`, background: 'transparent', color: f.text, fontSize: 13, cursor: 'pointer' }}>↻ {T('nochmal')}</button>}
              </div>
              {e.stand === 'wartetName' && (
                <div data-testid={`de-balken-${e.code}`} style={{ marginTop: 6, height: 6, borderRadius: 3, background: dark ? '#3a4049' : '#e0e0e0', overflow: 'hidden' }}>
                  <div style={{ width: `${fortschritt(e)}%`, height: '100%', background: '#d40511', transition: 'width 1s linear' }} />
                </div>
              )}
            </div>
          ))}
        </div>
        <button type="button" data-testid="de-schliessen" disabled={laeuft} onClick={() => { const n = fotosOffen(fotoStand); if (n && !window.confirm(T('fotosOffen', { n }))) return; onClose(); }} style={{ width: '100%', padding: '12px', borderRadius: 10, marginTop: 14, fontSize: 15, fontWeight: 600, cursor: laeuft ? 'default' : 'pointer', border: 'none', background: '#d40511', color: '#fff', opacity: laeuft ? 0.6 : 1 }}>
          {laeuft ? `⏳ ${T('wartet')}` : T('fertig')}
        </button>
      </div>
    </div>
  );
}
